//#region header */
/**************************************************************************************************
//
//  Description: Form to display the document information
//
//  Copyright:    © 2025 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Changed required for the V2 of the API.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useState } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";
import useApplicationNames from "../hooks/useApplicationNames";

import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import { ApplicationNameDto } from "../dtos/applicationNamesDto";
import ConfigErrorType from "../models/configErrorType";

import { Box, Grid2 } from "@mui/material";
import ADSTextControl from "../components/ADSTextControl";
import ADSUserDateControl from "../components/ADSUserDateControl";
import ADSReadOnlyControl from "../components/ADSReadOnlyControl";

import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";
import ADSSelectControl from "../components/ADSSelectControl";

interface DocumentFormProps {
  data: MultiTenantConfigGetDto;
  errors: ConfigErrorType[] | undefined;
  onDataChange: (data: MultiTenantConfigGetDto) => void;
}

const DocumentForm: React.FC<DocumentFormProps> = ({ data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);

  const [configType, setConfigType] = useState<string | undefined>();
  const [version, setVersion] = useState<number | undefined>();
  const [appName, setAppName] = useState<string | undefined>();
  const [name, setName] = useState<string | undefined>();
  const [created, setCreated] = useState<string>();
  const [lastUpdated, setLastUpdated] = useState<string>();
  const [createdBy, setCreatedBy] = useState<string>();
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string>();
  const [applicationNames, setApplicationNames] = useState<ApplicationNameDto[]>([]);
  const { data: applicationNamesData, isLoading: applicationNamesLoading } = useApplicationNames();

  const [appNameError, setAppNameError] = useState<string[] | undefined>();
  const [nameError, setNameError] = useState<string[] | undefined>();

  /**
   * Method to update the data.
   * @param fieldName The field name to update.
   * @param value The new value.
   */
  const updateData = (fieldName: string, value: string | number | undefined): void => {
    const updatedDocument = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedDocument);
    }
  };

  /**
   * Event to handle when the application name is changed.
   * @param newValue The new application name.
   */
  const handleAppNameChangeEvent = (newValue: string): void => {
    setAppName(newValue);
    updateData("appName", newValue);
  };

  /**
   * Event to handle when the name is changed.
   * @param newValue The new name.
   */
  const handleNameChangeEvent = (newValue: string): void => {
    setName(newValue);
    updateData("name", newValue);
  };

  useEffect(() => {
    if (applicationNamesLoading) return;

    const merged = [...(applicationNamesData ?? [])];
    if (data?.appName && !merged.some((app) => app.name === data.appName)) {
      merged.push({
        name: data.appName,
        dbConnectionNames: [],
        elasticConnectionNames: [],
      });
    }
    setApplicationNames(merged);
  }, [applicationNamesLoading, applicationNamesData, data?.appName]);

  useEffect(() => {
    if (data) {
      setConfigType(data.configType);
      setVersion(data.version);
      setAppName(data.appName);
      setName(data.name);
      setCreated(data.created);
      setLastUpdated(data.lastUpdated);
      setCreatedBy(data.createdBy);
      setLastUpdatedBy(data.lastUpdatedBy);
    }
  }, [data]);

  useEffect(() => {
    setAppNameError(undefined);
    setNameError(undefined);

    const documentKey = data.id || data.name || data.appName;
    const filteredErrors = errors?.filter(
      (e) => e.id === data.id || e.id === data.appName || e.id === data.name || e.treeId === documentKey
    );

    if (filteredErrors && filteredErrors.length > 0) {
      for (const error of filteredErrors) {
        switch (error.field) {
          case "appName":
            setAppNameError(error.errors);
            break;

          case "name":
            setNameError(error.errors);
            break;

          default:
            break;
        }
      }
    }
  }, [errors, data]);

  useEffect(() => {
    if (revertChanges && originalDocument) {
      setConfigType(originalDocument.configType);
      setVersion(originalDocument.version);
      setAppName(originalDocument.appName);
      setName(originalDocument.name);
      setCreated(originalDocument.created);
      setLastUpdated(originalDocument.lastUpdated);
      setCreatedBy(originalDocument.createdBy);
      setLastUpdatedBy(originalDocument.lastUpdatedBy);
    }
  }, [revertChanges, originalDocument]);

  return (
    <Box sx={dataFormStyle("DocumentForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSSelectControl
            label="Application name"
            value={appName}
            isEditable={currentUser?.isSuperAdministrator}
            lookupData={applicationNames}
            lookupId="name"
            lookupLabel="name"
            isRequired
            helperText="The application name of the document."
            errorText={appNameError}
            onChange={(id) => handleAppNameChangeEvent(id?.toString() || "")}
          />
          <ADSTextControl
            label="Name"
            value={name}
            maxLength={100}
            id="document_name"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The name of the document."
            errorText={nameError}
            onChange={handleNameChangeEvent}
          />
          <ADSReadOnlyControl
            label="Configuration type"
            value={configType}
            nullString="configuration type set on save"
          />
          <ADSReadOnlyControl label="Version" value={version?.toString()} nullString="version set on save" />
          <ADSUserDateControl label="Created by" user={createdBy} date={created} />
          <ADSUserDateControl label="Last updated by" user={lastUpdatedBy} date={lastUpdated} />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DocumentForm;
